/*
 * @Author: Ryan
 * @Date: 2026-03-11
 * @Description: Why Choose FalcoCut AI Twin Generator - 6 feature cards
 */
import Link from 'next/link';
import React from 'react';

interface ReasonCard {
  icon: string;
  title: string;
  description: string;
}

const reasons: ReasonCard[] = [
  {
    icon: '\u26A1\uFE0F',
    title: 'Unmatched Visual Fidelity',
    description:
      'Your digital twin looks and moves like a real person. Our technology captures natural facial expressions, so your audience stays focused on your message.',
  },
  {
    icon: '\uD83C\uDF10',
    title: 'Simple for Everyone',
    description:
      'Skip the technical headaches of traditional editing. Our intuitive interface allows you to move from a script to a finished export in just a few clicks.',
  },
  {
    icon: '\uD83E\uDDE9',
    title: 'Full Creative Control',
    description:
      'Take full command of your messaging. Tailor every word and movement to your specific goals, from educational tutorials to high-energy promotional ads and entertainment.',
  },
  {
    icon: '\uD83D\uDD12',
    title: 'Always Professional and On-Brand',
    description:
      'Keep your brand looking its best every time. You won\'t have to worry about lighting or "off-days," ensuring your videos are always high-quality and consistent.',
  },
  {
    icon: '\u2728',
    title: 'Cut Costs and Save Time',
    description:
      'Stop spending thousands on cameras, studios, and film crews. Our AI does the hard work for you, saving you both precious time and money.',
  },
  {
    icon: '\uD83D\uDCB0',
    title: 'Your Data is Protected',
    description:
      'We keep your face and voice data secure. Your digital identity is protected with top-level security, so your information always stays private and under your control.',
  },
];

const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

const ReasonCardItem: React.FC<ReasonCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut AI Twin Generator
        </h2>
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Empower your brand with a high-performance digital presence that works even when
          you don&apos;t. FalcoCut provides the ultimate balance of hyper-realism and
          operational efficiency to keep you ahead of the competition.
        </p>

        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6 mb-10 pad:mb-14">
          {reasons.map((reason, index) => (
            <ReasonCardItem
              key={index}
              icon={reason.icon}
              title={reason.title}
              description={reason.description}
            />
          ))}
        </div>

        <div className="flex justify-center">
          <Link
            href="/ai-video-generator"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
            }}
          >
            <span>Create Your AI Twin</span>
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
